// The compose page: Create, Read, Edit and Share (print) for a child who
// may not read yet, in speech-lab with nothing stored. State is the
// `reduceSession` store plus a small mode machine for the microphone; every
// transition happens in a handler or in the dictation hook's callbacks, and
// what the page shows is derived during render.
//
// Audio: the child's recordings stay in `session.clips` for this page's
// lifetime and are never uploaded except once, for transcription. Read-back
// is the browser's voice (./read-aloud.ts).
import {
  childCanConfirm,
  type CompositionToken,
  hasSpellingDoubt,
  type LadderRung,
  nextRung,
  sentences,
  surfaced,
  tokenHistory,
  tokenLineage,
  tokenize,
  type UnresolvedToken,
  type WordToken,
} from "@bindery/composition";
import { fetchToken } from "@bindery/auth-consumer/browser";
import type { Recording } from "@bindery/speech/capture";
import { submitRecording } from "@bindery/speech/client";
import { childFacing } from "@bindery/speech/contract";
import {
  FlaskConical,
  Keyboard,
  Printer,
  RotateCcw,
  UserRound,
} from "lucide-react";
import { useEffect, useReducer, useRef, useState } from "react";
import { type ClipOutcome, type ClipPlayback, playClip } from "./clip";
import { useDictation } from "./dictation";
import { newId } from "./ids";
import { requestWordIntent } from "./word-intent-client";
import { fixtureSightMap } from "./fixture-sight";
import {
  describeResultFor,
  type DescribeWordResponse,
} from "../contracts/describe-word";
import {
  WORD_INTENT_MAX_WORDS,
  wordIntentRequestFor,
} from "../contracts/word-intent";
import { requestDescribeWord } from "./describe-word-client";
import { type DescribePhase, DescribeRung } from "./describe-rung";
import { ComposeKeyboard, KeyboardSheet } from "./keyboard";
import { keyboardContext } from "./keyboard-gate";
import { type IntentEvent, LabPanel } from "./lab-panel";
import {
  captureOf,
  describePhaseOf,
  heldRung,
  type MicEffect,
  type MicEvent,
  type MicStep,
  type Mode,
  type Purpose,
  READY,
  reduceMic,
  reSayFollowUp,
  reSayOf,
  talkStateOf,
} from "./mic-mode";
import {
  alternativesOf,
  BUILT_IN_RUNGS,
  describeGap,
  describeLanding,
  describeLine,
  describeRequest,
  failedTranscriptionsOf,
  intentWindow,
  offerRung,
  spokenWords,
  transcriptionContext,
} from "./ladder";
import { PrintBook } from "./print-book";
import { inTurn, useReadAloud } from "./read-aloud";
import {
  type ComposeSession,
  insertIndexFor,
  newSession,
  reduceSession,
  reSayPreview,
  surfacingContext,
} from "./session";
import { Story, type WordMark } from "./story";
import { TalkButton } from "./talk-button";
import { type RungRenderers, WordSheet } from "./word-sheet";
import "./compose.css";

// Must match TRANSCRIBE_PATH in worker/index.ts.
const TRANSCRIBE_URL = "/api/speech/transcribe";
const TITLE = "My Story";

/** The "tell me about it" rung's progress for one word; the description itself is never kept. */
interface Describing {
  readonly tokenId: string;
  readonly phase: DescribePhase;
  readonly response: DescribeWordResponse | null;
}

type Notice = "try-again" | "grown-up";

const CUE: Readonly<Record<LadderRung, string>> = {
  "hear-and-compare": "Is that what you said?",
  choose: "Let's try picking it.",
  type: "Let's try typing it.",
  "re-say": "Say the whole sentence again.",
  "tell-me-about-it": "Tell me about that word. What is it like?",
  "save-for-adult": "Let's save this one for a grown-up.",
};

export function ComposePage({ rungs }: { rungs?: RungRenderers }) {
  const [session, dispatch] = useReducer(reduceSession, undefined, () => {
    const now = Date.now();
    return newSession({
      id: newId(),
      readerId: newId(),
      sight: fixtureSightMap(now),
      createdAt: now,
    });
  });
  // The latest session, for the dictation callbacks that outlive a render.
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  });

  const [mode, setModeState] = useState<Mode>(READY);
  // The mode the recorder's callbacks and the voice's chains act on, which
  // outlive the render that started them; `mode` is for what the page shows.
  const modeRef = useRef<Mode>(mode);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [lastError, setLastError] = useState<unknown>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [showLab, setShowLab] = useState(false);
  const [spelling, setSpelling] = useState(false);
  const [printedOn] = useState(() => new Date().toLocaleDateString("en-US"));
  // Alternatives an unjudged intent call brought back, for the choose rung;
  // kept here rather than in the log so an empty judgement hides no real one.
  const [offeredAlternatives, setOffered] = useState<
    ReadonlyMap<string, readonly string[]>
  >(() => new Map());
  const [intentLog, setIntentLog] = useState<readonly IntentEvent[]>([]);
  const [describing, setDescribing] = useState<Describing | null>(null);

  const readAloud = useReadAloud();
  const clipRef = useRef<ClipPlayback | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      clipRef.current?.cancel();
      inFlight.current?.abort();
    },
    [],
  );

  const stopAudio = (): void => {
    clipRef.current?.cancel();
    clipRef.current = null;
    readAloud.cancel();
  };

  // The recorder's own callbacks only `move`: none of their events asks
  // anything of the recorder, and they must not reach back into it.
  const move = (event: MicEvent): MicStep => {
    const next = reduceMic(modeRef.current, event);
    if (next.mode !== modeRef.current) {
      modeRef.current = next.mode;
      setModeState(next.mode);
    }
    return next;
  };
  const step = (event: MicEvent): void => run(move(event).effect);
  /** Every way a word's sheet closes passes here, so its recording ends with it. */
  const leaveWord = (tokenId: string): void =>
    step({ type: "word-closed", tokenId });

  const run = (effect: MicEffect | null): void => {
    switch (effect) {
      case "open-mic":
        if (dictation.state.tag === "unsupported") {
          setNotice("grown-up");
          move({ type: "failed" });
          return;
        }
        setNotice(null);
        dictation.start();
        return;
      case "stop-mic":
        dictation.stop();
        return;
      case "discard":
        inFlight.current?.abort();
        inFlight.current = null;
        dictation.discard();
        return;
      case "read-story":
        void readAloud
          .read(story)
          .then((outcome) =>
            step({ type: "read-back-ended", outcome, captureId: newId() }),
          );
        return;
      case "transcribe":
      case null:
        return;
    }
  };

  // This host renders the keyboard rung itself; a `rungs` prop may replace
  // it or add "tell me about it".
  const hostRungs: RungRenderers = {
    type: ({ token, commit, skip }) => (
      <ComposeKeyboard
        context={keyboardContext(
          session.composition.tokens,
          session.composition.tokens.findIndex((t) => t.id === token.id),
        )}
        onCommit={(word) => commit(word)}
        onSayInstead={skip}
        onSaveForAdult={() => {
          stopAudio();
          leaveWord(token.id);
          dispatch({
            type: "save-for-adult",
            tokenId: token.id,
            at: Date.now(),
          });
          void readAloud.say("Saved for a grown-up.");
        }}
        onSay={(text) => {
          stopAudio();
          void readAloud.say(text);
        }}
      />
    ),
    "tell-me-about-it": ({ token }) => (
      <DescribeRung
        phase={describePhase(token.id)}
        busy={busy}
        level={dictation.level}
        onStart={() => {
          stopAudio();
          setDescribing({
            tokenId: token.id,
            phase: { kind: "ask" },
            response: null,
          });
          listen({ kind: "describe", tokenId: token.id });
        }}
        onStop={() => step({ type: "stop" })}
        onHear={(word) => {
          stopAudio();
          void readAloud.say(word);
        }}
        onChoose={(word) => settleDescription(token, { kind: "chose", word })}
        onNoneOfThese={() =>
          settleDescription(token, { kind: "none-of-these" })
        }
        onSave={() => {
          stopAudio();
          leaveWord(token.id);
          setDescribing(null);
          dispatch({
            type: "save-for-adult",
            tokenId: token.id,
            at: Date.now(),
          });
          void readAloud.say("Saved for a grown-up.");
        }}
      />
    ),
    ...rungs,
  };
  const supported = new Set<LadderRung>([
    ...BUILT_IN_RUNGS,
    ...(Object.keys(hostRungs) as LadderRung[]),
  ]);

  const handleRecorded = async (
    recording: Recording,
    at: number,
  ): Promise<void> => {
    const { mode: thinking, effect } = move({ type: "recorded" });
    const capture = captureOf(thinking);
    if (effect !== "transcribe" || capture === null) return;
    const { purpose } = capture;
    const settle = () => move({ type: "transcribed", captureId: capture.id });
    const dictationId = newId();
    const controller = new AbortController();
    inFlight.current = controller;
    const context = transcriptionContext(sessionRef.current.composition.tokens);
    const response = await submitRecording({
      url: TRANSCRIBE_URL,
      recording,
      fields: {
        promptText: "",
        // One item per dictation, so the lab's per-item attempt cap never
        // meets a child telling a long story.
        itemRef: { kind: "composition", itemId: dictationId },
        attempt: 1,
        ...(context === "" ? {} : { transcriptionContext: context }),
      },
      getToken: fetchToken,
      signal: controller.signal,
    });
    if (inFlight.current !== controller) return;
    inFlight.current = null;
    if (response.status === "error") {
      setLastError(response.error);
      if (purpose.kind === "re-say" && response.error.kind === "no-speech") {
        dispatch({ type: "re-say-no-speech", spanIds: purpose.spanIds, at });
      }
      if (purpose.kind === "describe") {
        setDescribing({
          tokenId: purpose.tokenId,
          phase: { kind: "ask" },
          response: null,
        });
      }
      setNotice(
        childFacing(response.error) === "try-again" ? "try-again" : "grown-up",
      );
      settle();
      return;
    }
    const { transcript, provenance } = response;
    if (purpose.kind === "describe") {
      settle();
      // The description is used once, here, and kept nowhere.
      await describeWord(purpose.tokenId, transcript.raw);
      return;
    }
    const tokens = tokenize(transcript.raw, transcript.words, {
      newId,
      dictationId,
      flagged: transcript.flagged,
      ...(transcript.flaggedSpans === undefined
        ? {}
        : { flaggedSpans: transcript.flaggedSpans }),
    });
    const landed = {
      tokens,
      asr: provenance,
      dictationId,
      clip: recording.blob,
      at,
    };
    setNotice(null);
    settle();
    if (purpose.kind === "dictate") {
      const action = { type: "dictation", ...landed } as const;
      const next = reduceSession(sessionRef.current, action);
      dispatch(action);
      // A dictation the reducer would not take (no words, or an edit the
      // package refused) is not on the page, so it is not read as if it were.
      if (next.composition === sessionRef.current.composition) {
        setNotice("try-again");
        return;
      }
      void readAloud.read(spokenWords(tokens));
      void checkIntent(next, tokens);
      return;
    }
    const sheetOpen = purpose.spanIds.some(
      (id) => id === sessionRef.current.focus,
    );
    const preview = reSayPreview(sessionRef.current, purpose.spanIds, tokens);
    const action = {
      type: "re-say",
      spanIds: purpose.spanIds,
      ...landed,
    } as const;
    const next = reduceSession(sessionRef.current, action);
    dispatch(action);
    // The package refuses a re-say past the cap, or for a word no longer
    // open, even if a stale sheet still offered the microphone; a refused
    // re-say is read back as nothing and the ladder moves on.
    const replacement =
      preview?.outcome === "replaced" &&
      next.composition !== sessionRef.current.composition
        ? preview.tokens
        : null;
    if (replacement !== null) void checkIntent(next, replacement);
    switch (reSayFollowUp(replacement !== null, sheetOpen)) {
      case "read-back":
        void inTurn([
          () => readAloud.read(spokenWords(replacement ?? [])),
          () => readAloud.say(CUE["hear-and-compare"]),
        ]);
        return;
      case "try-another-way":
        void readAloud.say("Hmm. Let's try another way.");
        return;
      case null:
        return;
    }
  };

  // The intent check runs after the sentence is on the page (plan § 4,
  // point 8): a `judge` edit only when the model answered, so an empty
  // judgement never hides a real one; an unjudged call's alternatives still
  // serve the choose rung.
  const checkIntent = async (
    landedIn: ComposeSession,
    fresh: readonly CompositionToken[],
  ): Promise<void> => {
    const context = surfacingContext(landedIn);
    const freshIds = fresh.flatMap((t) => (t.kind === "word" ? [t.id] : []));
    const noDictionary = () =>
      dispatch({
        type: "dictionary-unknown",
        tokenIds: freshIds,
        at: Date.now(),
      });
    // The questions are about the words that just landed, under the per-call
    // cap; as much of the story as the request takes goes along as context,
    // windowed so the judged words are always among it.
    const asked = wordIntentRequestFor(fresh, context);
    if (asked === null) {
      noDictionary();
      return;
    }
    const judge = asked.judge;
    const words = intentWindow(
      landedIn.composition.tokens.flatMap((t) =>
        t.kind === "word" ? [{ id: t.id, text: t.text }] : [],
      ),
      judge,
      WORD_INTENT_MAX_WORDS,
    );
    const started = performance.now();
    const result = await requestWordIntent({
      request: { words, judge },
      getToken: fetchToken,
      now: Date.now,
    });
    const event: IntentEvent = {
      outcome: result.kind,
      ms: Math.round(performance.now() - started),
      asked: judge.length,
      judged:
        "judgements" in result
          ? result.judgements.filter((j) => j.judgement.model !== null).length
          : 0,
    };
    setIntentLog((log) => [...log, event]);
    if (!("judgements" in result)) {
      noDictionary();
      return;
    }
    // The dictionary answer comes in every outcome and needs no vendor; it
    // is the surfacing rule's non-dictionary signal (plan § 4, Edit).
    dispatch({
      type: "dictionary",
      entries: result.dictionary,
      at: Date.now(),
    });
    if (result.kind === "judged") {
      dispatch({
        type: "judge",
        judgements: result.judgements,
        at: Date.now(),
      });
      return;
    }
    setOffered(
      (map) =>
        new Map([
          ...map,
          ...result.judgements.map(
            (j) =>
              [j.tokenId, j.judgement.alternatives.map((a) => a.word)] as const,
          ),
        ]),
    );
  };

  const describeWord = async (
    tokenId: string,
    description: string,
  ): Promise<void> => {
    const current = sessionRef.current;
    const token = current.composition.tokens.find((t) => t.id === tokenId);
    const gap = describeGap(current.composition.tokens, tokenId);
    if (token?.kind !== "word" || gap === null) {
      setDescribing(null);
      return;
    }
    const failed = failedTranscriptionsOf(
      current.composition.edits,
      tokenLineage(token),
      token.text,
    );
    const request = describeRequest(gap, failed, description);
    if (request === null) {
      setDescribing({ tokenId, phase: { kind: "again" }, response: null });
      return;
    }
    setDescribing({ tokenId, phase: { kind: "thinking" }, response: null });
    const result = await requestDescribeWord({
      request,
      getToken: fetchToken,
    });
    const after = sessionRef.current;
    const landing = describeLanding(
      result,
      after.composition.tokens.find((t) => t.id === tokenId),
    );
    const line = describeLine(landing, after.focus === tokenId);
    if (line !== null) void readAloud.say(line);
    switch (landing.kind) {
      case "drop":
        setDescribing((d) => (d?.tokenId === tokenId ? null : d));
        return;
      case "again":
        setLastError(landing.reason);
        setDescribing({ tokenId, phase: { kind: "again" }, response: null });
        return;
      case "offer":
        setDescribing({
          tokenId,
          phase: { kind: "offer", candidates: landing.candidates },
          response: landing.response,
        });
        return;
      case "nothing":
        dispatch({
          type: "describe",
          tokenId,
          failedTranscriptions: failed,
          result: { outcome: "no-candidates" },
          at: Date.now(),
        });
        setDescribing({ tokenId, phase: { kind: "nothing" }, response: null });
        return;
    }
  };

  const settleDescription = (
    token: WordToken,
    choice: { kind: "chose"; word: string } | { kind: "none-of-these" },
  ): void => {
    const response =
      describing?.tokenId === token.id ? describing.response : null;
    if (response === null) return;
    stopAudio();
    dispatch({
      type: "describe",
      tokenId: token.id,
      failedTranscriptions: failedTranscriptionsOf(
        session.composition.edits,
        tokenLineage(token),
        token.text,
      ),
      result: describeResultFor(response, choice),
      at: Date.now(),
    });
    setDescribing(null);
    void readAloud.say(
      choice.kind === "chose" ? "Got it." : CUE["save-for-adult"],
    );
  };

  const describePhase = (tokenId: string): DescribePhase =>
    describePhaseOf(
      mode,
      tokenId,
      describing?.tokenId === tokenId ? describing.phase : { kind: "ask" },
    );

  const captureFailed = (notice: Notice, error: unknown): void => {
    setLastError(error);
    setNotice(notice);
    move({ type: "failed" });
  };

  const dictation = useDictation({
    onRecorded: (recording) => void handleRecorded(recording, Date.now()),
    onRefused: (error) => captureFailed("try-again", error),
    onBlocked: (error) => captureFailed("grown-up", error),
  });

  const tokens = session.composition.tokens;
  const story = spokenWords(tokens);

  // One capture at a time: while a recording is open or being written down,
  // no other rung may open the microphone.
  const busy = captureOf(mode) !== null;

  const listen = (purpose: Purpose): void => {
    step({ type: "listen", purpose, captureId: newId() });
  };

  const onTalk = (): void => {
    stopAudio();
    step({ type: "talk", storyEmpty: story.length === 0, captureId: newId() });
  };

  const hear = (token: WordToken | UnresolvedToken): void => {
    stopAudio();
    const clip =
      token.asr === null ? undefined : session.clips.get(token.asr.dictationId);
    const own = (): Promise<ClipOutcome> => {
      if (clip === undefined || token.asr === null) {
        return Promise.resolve("done");
      }
      const playback = playClip(clip, token.asr.startMs, token.asr.endMs);
      clipRef.current = playback;
      return playback.done;
    };
    void inTurn(
      token.kind === "word" ? [own, () => readAloud.say(token.text)] : [own],
    );
  };

  const focused =
    session.focus === null
      ? null
      : (tokens.find((t) => t.id === session.focus) ?? null);
  const focusedWord = focused?.kind === "word" ? focused : null;
  const history =
    focused === null || focused.kind === "punct"
      ? []
      : tokenHistory(session.composition.edits, tokenLineage(focused));
  const alternatives =
    focusedWord === null
      ? []
      : alternativesOf(
          focusedWord,
          offeredAlternatives.get(focusedWord.id) ?? [],
        );
  const surfacing = surfacingContext(session);
  const ladder = nextRung(history, {
    hasAlternatives: alternatives.length > 0,
    childCanConfirm:
      focusedWord === null || childCanConfirm(focusedWord, surfacing),
  });
  const offered =
    (focusedWord === null ? null : heldRung(mode, focusedWord.id)) ??
    offerRung(ladder, supported);

  const cueAfter = (
    word: WordToken,
    rung: "hear-and-compare" | "choose" | "type",
    at: number,
  ): void => {
    dispatch({ type: "reject", tokenId: word.id, rung, at });
    const next = offerRung(
      nextRung([...history, { op: "reject", tokenId: word.id, rung, at }], {
        hasAlternatives:
          alternativesOf(word, offeredAlternatives.get(word.id) ?? []).length >
          0,
        childCanConfirm: childCanConfirm(word, surfacing),
      }),
      supported,
    );
    if (next !== null) void readAloud.say(CUE[next]);
  };

  const surfacings = surfaced(tokens, surfacing);
  const marks = new Map<string, WordMark>(
    surfacings.map((s) => [
      s.tokenId,
      s.reason === "transcription-doubt" ? "doubt" : "new",
    ]),
  );
  const unvouched = new Set(
    surfacings.filter(hasSpellingDoubt).map((s) => s.tokenId),
  );
  const lit =
    readAloud.reading === null || readAloud.reading.index === null
      ? null
      : (readAloud.reading.words[readAloud.reading.index]?.tokenId ?? null);

  const talkState = talkStateOf(mode, dictation.state.tag !== "unsupported");
  const reSay = focusedWord === null ? null : reSayOf(mode, focusedWord.id);

  const hasClip = (token: WordToken | UnresolvedToken): boolean =>
    token.asr !== null && session.clips.has(token.asr.dictationId);

  const sentenceWords = (index: number) => {
    const ids = new Set(sentences(tokens)[index] ?? []);
    return story.filter((w) => ids.has(w.tokenId));
  };

  return (
    <div className="compose -mx-4 -my-4 flex flex-col">
      <div className="compose-screen flex flex-1 flex-col items-center gap-6 px-4 pt-4 pb-40">
        <div className="flex w-full max-w-3xl items-center justify-between">
          <a
            href="#bench"
            className="text-sm font-bold"
            style={{ color: "oklch(0.98 0.01 85)" }}
          >
            ← bench
          </a>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="compose-icon"
              aria-label="Print my story"
              disabled={story.length === 0}
              onClick={() => window.print()}
            >
              <Printer className="size-6" />
            </button>
            <button
              type="button"
              className="compose-icon"
              aria-label="Researcher panel"
              aria-pressed={showLab}
              style={{ opacity: 0.7 }}
              onClick={() => setShowLab((v) => !v)}
            >
              <FlaskConical className="size-6" />
            </button>
          </div>
        </div>

        <main className="compose-paper w-full max-w-3xl px-5 py-6 sm:px-8">
          <h1
            className="m-0 text-3xl font-bold"
            style={{ lineHeight: "var(--line)" }}
          >
            {TITLE}
          </h1>
          {story.length === 0 && tokens.length === 0 ? (
            <p className="m-0 text-2xl" style={{ color: "var(--ink-soft)" }}>
              Tap the sun and tell me what happened.
            </p>
          ) : null}
          <Story
            tokens={tokens}
            marks={marks}
            lit={lit}
            open={session.focus}
            cursor={session.cursor}
            selected={selected}
            hasClip={hasClip}
            onTapWord={(token) => {
              setSelected(null);
              dispatch({ type: "focus", tokenId: token.id });
              hear(token);
            }}
            onTapSlot={(token) => {
              setSelected(null);
              dispatch({ type: "focus", tokenId: token.id });
              hear(token);
            }}
            onTapGap={(index) => {
              setSelected(null);
              dispatch({ type: "cursor", sentence: index });
            }}
            onSelectSentence={setSelected}
            onHearSentence={(index) => {
              stopAudio();
              void readAloud.read(sentenceWords(index));
            }}
            onMoveSentence={(from, to) => {
              dispatch({ type: "move-sentence", from, to, at: Date.now() });
              setSelected(to);
            }}
            onDeleteSentence={(index) => {
              stopAudio();
              dispatch({
                type: "delete-sentence",
                sentence: index,
                at: Date.now(),
              });
              setSelected(null);
              void readAloud.say("Okay, that part is out.");
            }}
          />
        </main>

        <div className="flex flex-col items-center gap-3">
          <div className="flex items-end gap-6">
            <TalkButton
              state={talkState}
              level={dictation.level}
              onTap={onTalk}
            />
            <div className="flex flex-col items-center gap-2 pb-9">
              <button
                type="button"
                className="compose-icon"
                style={{ width: "4.5rem", height: "4.5rem" }}
                aria-label="Spell a word with the keyboard"
                disabled={mode.kind !== "ready"}
                onClick={() => {
                  stopAudio();
                  setSelected(null);
                  setSpelling(true);
                }}
              >
                <Keyboard className="size-9" />
              </button>
              <span
                className="text-base"
                style={{ color: "oklch(0.98 0.01 85)" }}
              >
                or spell it
              </span>
            </div>
          </div>
          {notice === null ? null : (
            <output
              className="compose-nod m-0 flex items-center gap-2 rounded-full px-5 py-3 text-lg"
              style={{ background: "var(--paper)", color: "var(--ink)" }}
            >
              {notice === "try-again" ? (
                <>
                  <RotateCcw className="size-6" /> I didn&rsquo;t catch that.
                  Tap and say it again.
                </>
              ) : (
                <>
                  <UserRound className="size-6" /> Let&rsquo;s ask a grown-up
                  for help.
                </>
              )}
            </output>
          )}
        </div>

        {showLab ? (
          <div
            className="text-foreground w-full pt-6"
            style={{ fontFamily: "var(--font-sans)" }}
          >
            <LabPanel
              session={session}
              capture={dictation.state}
              level={dictation.level}
              mode={mode.kind}
              lastError={lastError}
              intentLog={intentLog}
              onSurface={(tokenId) =>
                dispatch({
                  type: "surface",
                  tokenIds: [tokenId],
                  at: Date.now(),
                })
              }
            />
          </div>
        ) : null}
      </div>

      {spelling && focused === null ? (
        <KeyboardSheet
          title="Spell a word, one letter at a time"
          onClose={() => setSpelling(false)}
        >
          <ComposeKeyboard
            context={keyboardContext(tokens, insertIndexFor(session))}
            onCommit={(word) => {
              const action = {
                type: "insert-typed",
                tokenId: newId(),
                text: word,
                at: Date.now(),
              } as const;
              const next = reduceSession(session, action);
              dispatch(action);
              if (next.composition !== session.composition) {
                void readAloud.say(word);
              }
            }}
            onSayInstead={() => {
              setSpelling(false);
              listen({ kind: "dictate" });
            }}
            onSay={(text) => {
              stopAudio();
              void readAloud.say(text);
            }}
          />
        </KeyboardSheet>
      ) : null}

      {focused !== null && focused.kind !== "punct" ? (
        <WordSheet
          token={focused}
          ladder={ladder}
          offered={offered}
          alternatives={alternatives}
          hasClip={hasClip(focused)}
          listening={reSay === "listening"}
          thinking={reSay === "thinking"}
          busy={busy}
          level={dictation.level}
          rungs={hostRungs}
          onHear={() => hear(focused)}
          onYes={() => {
            if (focusedWord === null) return;
            leaveWord(focusedWord.id);
            dispatch({
              type: "confirm",
              tokenId: focusedWord.id,
              at: Date.now(),
            });
            void readAloud.say("Got it.");
          }}
          onNotQuite={() =>
            focusedWord !== null &&
            cueAfter(focusedWord, "hear-and-compare", Date.now())
          }
          onHearAlternative={(word) => {
            stopAudio();
            void readAloud.say(word);
          }}
          onChoose={(to) => {
            if (focusedWord === null) return;
            leaveWord(focusedWord.id);
            dispatch({
              type: "choose",
              tokenId: focusedWord.id,
              from: focusedWord.text,
              to,
              at: Date.now(),
            });
            void readAloud.say("Got it.");
          }}
          onNoneOfThese={() =>
            focusedWord !== null && cueAfter(focusedWord, "choose", Date.now())
          }
          onReSay={() => {
            stopAudio();
            if (focusedWord !== null)
              listen({ kind: "re-say", spanIds: [focusedWord.id] });
          }}
          onStopReSay={() => step({ type: "stop" })}
          onTypeIt={(to) => {
            if (focusedWord === null) return;
            leaveWord(focusedWord.id);
            dispatch({
              type: "type",
              tokenId: focusedWord.id,
              from: focusedWord.text,
              to,
              at: Date.now(),
            });
            void readAloud.say("Got it.");
          }}
          onSkipRung={(rung) =>
            focusedWord !== null && cueAfter(focusedWord, rung, Date.now())
          }
          onSave={() => {
            if (focusedWord === null) return;
            stopAudio();
            leaveWord(focusedWord.id);
            dispatch({
              type: "save-for-adult",
              tokenId: focusedWord.id,
              at: Date.now(),
            });
            void readAloud.say("Saved for a grown-up.");
          }}
          onClose={() => {
            stopAudio();
            leaveWord(focused.id);
            dispatch({ type: "focus", tokenId: null });
          }}
        />
      ) : null}

      <PrintBook
        tokens={tokens}
        blanks={unvouched}
        title={TITLE}
        printedOn={printedOn}
      />
    </div>
  );
}
